import type maplibregl from 'maplibre-gl';

import type { VesselIconKind } from '../data/vesselEnrich';

export const VESSEL_ICON_BASE = '/icons/vessels/';
export const VESSEL_CANVAS_PX = 96;
export const VESSEL_TINT_ALPHA = 0.42;

export const VESSEL_ICON_DEFS: Record<
  VesselIconKind,
  { file: string; color: string }
> = {
  tanker: { file: 'tanker.svg', color: '#f97316' },
  cargo: { file: 'cargo.svg', color: '#38bdf8' },
  container: { file: 'container.svg', color: '#22d3ee' },
  passenger: { file: 'passenger.svg', color: '#a78bfa' },
  fishing: { file: 'fishing.svg', color: '#4ade80' },
  military: { file: 'military.svg', color: '#fb923c' },
  tug: { file: 'tug.svg', color: '#fbbf24' },
  pleasure: { file: 'pleasure.svg', color: '#c4b5fd' },
  unknown: { file: 'unknown.svg', color: '#94a3b8' },
};

export const VESSEL_ICON_IMAGE_EXPR = ['coalesce', ['get', 'iconImage'], 'ship-unknown'] as const;

export const VESSEL_COLOR_EXPR = [
  'match',
  ['get', 'icon'],
  'tanker',
  '#f97316',
  'cargo',
  '#38bdf8',
  'container',
  '#22d3ee',
  'passenger',
  '#a78bfa',
  'fishing',
  '#4ade80',
  'military',
  '#fb923c',
  'tug',
  '#fbbf24',
  'pleasure',
  '#c4b5fd',
  '#94a3b8',
] as const;

export const VESSEL_ICON_SIZE_EXPR = [
  'interpolate',
  ['linear'],
  ['zoom'],
  2,
  0.1,
  5,
  0.16,
  8,
  0.24,
  12,
  0.34,
] as const;

const svgCache = new Map<string, string>();

function normalizeSvg(svg: string): string {
  if (!/<svg\b/i.test(svg)) return svg;
  return svg.replace(/<svg\b([^>]*)>/i, (full, attrs) => {
    const w = /\bwidth=/i.test(attrs) ? '' : ' width="96"';
    const h = /\bheight=/i.test(attrs) ? '' : ' height="96"';
    return full.replace(attrs, `${attrs}${w}${h}`);
  });
}

async function fetchSvg(file: string): Promise<string> {
  const cached = svgCache.get(file);
  if (cached) return cached;
  const text = await fetch(`${VESSEL_ICON_BASE}${file}`).then((r) => (r.ok ? r.text() : ''));
  if (text) svgCache.set(file, text);
  return text;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('vessel icon failed to load'));
    img.src = src;
  });
}

function drawTintedIcon(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  color: string,
  size = VESSEL_CANVAS_PX,
  tintAlpha = VESSEL_TINT_ALPHA,
): ImageData {
  const iw = img.naturalWidth || size;
  const ih = img.naturalHeight || size;
  const scale = Math.min(size / iw, size / ih);
  const w = iw * scale;
  const h = ih * scale;
  const x = (size - w) / 2;
  const y = (size - h) / 2;
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, x, y, w, h);
  if (tintAlpha <= 0) return ctx.getImageData(0, 0, size, size);
  const tint = document.createElement('canvas');
  tint.width = size;
  tint.height = size;
  const tctx = tint.getContext('2d')!;
  tctx.drawImage(img, x, y, w, h);
  tctx.globalCompositeOperation = 'color';
  tctx.fillStyle = color;
  tctx.fillRect(0, 0, size, size);
  tctx.globalCompositeOperation = 'destination-in';
  tctx.drawImage(img, x, y, w, h);
  ctx.globalAlpha = tintAlpha;
  ctx.drawImage(tint, 0, 0);
  ctx.globalAlpha = 1;
  return ctx.getImageData(0, 0, size, size);
}

async function svgToImageData(svg: string, color: string): Promise<ImageData | null> {
  const blob = new Blob([normalizeSvg(svg)], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = VESSEL_CANVAS_PX;
    canvas.height = VESSEL_CANVAS_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    return drawTintedIcon(ctx, img, color);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function addVesselIcon(map: maplibregl.Map, id: string, def: { file: string; color: string }) {
  if (map.hasImage(id)) return true;
  const svg = await fetchSvg(def.file);
  if (!svg) return false;
  const data = await svgToImageData(svg, def.color);
  if (!data) return false;
  try {
    map.addImage(id, data);
    return true;
  } catch {
    return false;
  }
}

export async function loadVesselIcons(map: maplibregl.Map): Promise<boolean> {
  const results = await Promise.all(
    Object.entries(VESSEL_ICON_DEFS).map(([kind, def]) => addVesselIcon(map, `ship-${kind}`, def)),
  );
  return results.every(Boolean);
}

export function vesselIconImageId(kind: VesselIconKind): string {
  return `ship-${kind}`;
}
